import { useEffect, useRef, useState } from "react";
import {
  createJob,
  uploadToPresignedUrl,
  getJobProgress,
  createOrder,
  modelFileName,
  METRICS_MODEL_FILENAME,
} from "../../services/remote-api";
import { createZipBlob } from "../../services/zip";

const PROGRESS_POLL_MS = 2000;
const PROGRESS_TIMEOUT_MS = 10 * 60 * 1000; // give up polling after 10 minutes

// Encapsulates the server side of a scan: creating the vision job, zipping
// and uploading the captured frames, polling the processing progress, and
// finally saving the order data.
//
// uploadState:
//   null (idle) | "uploading" | "processing" | "succeeded" | "failed" | "error"
// "failed" = the job itself failed (rescan only);
// "error" = a transient/other error (retry upload or rescan).
export function useScanJob({ order, fileFormat, title, paperType }) {
  // order fields sent when creating the vision job.
  const { labId, webOrderId, webOrderUserName } = order.order;
  const { bodyPart } = order.order.bodyGroup;

  const [uploadState, setUploadState] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [modelUrl, setModelUrl] = useState(null); // presigned URL of the result
  // when that URL was issued: it expires ~10 minutes later, and the scan may
  // be reviewed for a while before it is saved.
  const [modelUrlAt, setModelUrlAt] = useState(0);
  // { jobId, dataUrl, modelNames } from the successful job, needed to review
  // and save the data later.
  const [jobData, setJobData] = useState(null);
  const [saved, setSaved] = useState(false); // data saved -> terminal state
  const pollRef = useRef(null);
  const pollDeadlineRef = useRef(0);
  const framesRef = useRef([]); // the frames being uploaded (kept for retries)
  // upload generation — bumped on every new upload/reset so that responses and
  // poll timers of a superseded job are ignored instead of fighting over the
  // progress state.
  const uploadSeqRef = useRef(0);

  // Actual paper type passed to the request
  const actualPaperType = order.visionUsePaper
    ? (paperType ?? "us-letter")
    : null;

  // stop polling when the consumer unmounts.
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearTimeout(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  // upload the given captured frames (JPEG Blobs) to a new vision job.
  function uploadScan(frames) {
    framesRef.current = frames;
    return upload();
  }

  // retry the upload with the same frames after a transient error.
  function retryUpload() {
    return upload();
  }

  // create the vision job, then zip the captured frames and PUT them to the
  // presigned S3 URL returned by the job.
  async function upload() {
    const seq = ++uploadSeqRef.current;
    setUploadError(null);
    setUploadState("uploading");
    try {
      const job = await createJob({
        relatedLabId: labId,
        scanType: bodyPart,
        webOrderId,
        webOrderUserName,
      });

      // zip the frames (named sequentially) into the job's data file.
      const entries = await Promise.all(
        framesRef.current.map(async (blob, i) => ({
          name: `rgb_${String(i).padStart(3, "0")}.jpg`,
          data: new Uint8Array(await blob.arrayBuffer()),
        })),
      );

      // also add the scan parameters as the "photogrammetryData.json" file.
      const scanParameter = {
        ...order.order.bodyGroup,
        useQRCode: order.visionUseQRCode,
        useCreditCard: order.visionUseCreditCard,
        usePaper: order.visionUsePaper,
        paperType: actualPaperType,
      };
      entries.push({
        name: "photogrammetryData.json",
        data: new TextEncoder().encode(JSON.stringify(scanParameter)),
      });

      const zipBlob = createZipBlob(entries);

      await uploadToPresignedUrl(job.presignedUrl, zipBlob);
      if (seq !== uploadSeqRef.current) return; // superseded meanwhile

      // uploaded — now track the server-side processing progress.
      setProgress(0);
      setUploadState("processing");
      pollDeadlineRef.current = Date.now() + PROGRESS_TIMEOUT_MS;
      pollJobProgress(job.jobId, seq);
    } catch (err) {
      if (seq !== uploadSeqRef.current) return;
      setUploadError(err.message);
      setUploadState("error");
    }
  }

  // poll job progress until the job succeeds, fails, or times out.
  async function pollJobProgress(jobId, seq) {
    if (seq !== uploadSeqRef.current) return; // job superseded — stop polling
    try {
      const status = await getJobProgress(jobId);
      if (seq !== uploadSeqRef.current) return;
      // scale the processing progress to 10-100% (0-10% is for data uploading).
      setProgress(status.progress * 0.9 + 10);

      if (status.jobStatus === "Succeed") {
        // use the first presigned URL to view the processed model.
        const urls = status.presignedUrls;
        setModelUrl(urls && urls.length ? urls[0] : null);
        setModelUrlAt(Date.now());
        // save other job info for the later save-data call.
        setJobData({
          jobId,
          dataUrl: status.dataUrl,
          modelNames: status.modelNames,
        });
        setUploadState("succeeded");
      } else if (status.jobStatus === "Failed") {
        // the job itself failed — rescan.
        setUploadError(
          status.failureMessage || "Processing failed for unknown reasons.",
        );
        setUploadState("failed");
      } else if (Date.now() >= pollDeadlineRef.current) {
        setUploadError(
          "Processing is taking longer than expected. Please try again later.",
        );
        setUploadState("error");
      } else {
        // pending / in progress / server retry — check again shortly.
        pollRef.current = setTimeout(
          () => pollJobProgress(jobId, seq),
          PROGRESS_POLL_MS,
        );
      }
    } catch (err) {
      if (seq !== uploadSeqRef.current) return;
      setUploadError(err.message);
      setUploadState("error");
    }
  }

  // discard the current job (for a fresh scan).
  function resetJob() {
    uploadSeqRef.current += 1; // invalidate any in-flight upload/poll
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
    framesRef.current = [];
    setProgress(0);
    setModelUrl(null);
    setModelUrlAt(0);
    setJobData(null);
    setUploadError(null);
    setUploadState(null);
  }

  // save and upload the model data via POST /api/order.
  // measurement: the scan's automatic measurement results ({ jobId, dataUrl,
  // ... }) when the user ran it; those are uploaded with the scans too where
  // the lab asks for them (when exportMetricsData == true).
  async function saveData({ measurement = null } = {}) {
    const oldName = jobData && jobData.modelNames && jobData.modelNames[0];
    const { webOrderDetails, extraData } = order.order;
    const modelName = modelFileName(title);
    const exportMetrics = Boolean(order.exportMetricsData && measurement);
    await createOrder({
      relatedLabId: labId,
      // single-scan mode: always exactly one scan type, quantity 1.
      scanTypes: [{ type: `${bodyPart}_VS`, number: 1 }],
      webOrderId,
      webOrderUserName,
      webOrderDetails,
      extraData: extraData ? JSON.stringify(extraData) : undefined,
      dataUrls: [jobData && jobData.dataUrl],
      modelNames: [
        { old: oldName, new: modelName, format: fileFormat ?? "stl" },
      ],
      // single-scan mode: the one scan is measured or not.
      metricsApiUsed: measurement ? 1 : 0,
      metricsDataUrls: exportMetrics ? [measurement.dataUrl] : undefined,
      metricsModelNames: exportMetrics
        ? [{ old: METRICS_MODEL_FILENAME, new: modelName }]
        : undefined,
    });
    setSaved(true);
  }

  return {
    uploadState,
    uploadError,
    progress,
    modelUrl,
    modelUrlAt,
    jobData,
    saved,
    uploadScan,
    retryUpload,
    resetJob,
    saveData,
  };
}
